import type { AnyMessageEvent, AnyMessageEventData, HandlerPayload } from "../iframe/types.js";
import { startWindowHandler, registerEventHandler, subscribeForDOMMutations } from "../iframe/index.js";
import {
  ColorTheme,
  type ColorThemeChangeEventPayload,
  type OnColorThemeChanged,
  type OnColorThemeChangedSubscriberFn,
} from "./types.js";
export { ColorTheme } from "./types.js";

const COLOR_THEME_CHANGED_EVENT = "colorThemeChanged";
const POLARIS_DARK_MODE_CLASS = "awsui-polaris-dark-mode";
const BROWSER_DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

const subscribers = new Set<OnColorThemeChangedSubscriberFn>();
let currentColorTheme: ColorTheme = ColorTheme.Light;
let isColorThemeTracked = false;

/**
 * Exposed for testing purposes. You should not call this function directly outside of your unit tests.
 */
export const updateColorThemeToSubscribers = (data: ColorThemeChangeEventPayload) => {
  for (const subscriber of subscribers) {
    subscriber({ newColorTheme: data.colorTheme });
  }
};

export const updateColorThemeClassNamesForEl = (el: HTMLElement, colorTheme: ColorTheme) => {
  if (el !== null) {
    if (colorTheme === ColorTheme.Dark) {
      el.classList.add(POLARIS_DARK_MODE_CLASS);
    } else {
      el.classList.remove(POLARIS_DARK_MODE_CLASS);
    }
  }
};

const updateColorThemeClassNamesInIframe = (iframe: HTMLIFrameElement, colorTheme: ColorTheme) => {
  const { contentDocument } = iframe;
  if (contentDocument) {
    updateColorThemeClassNamesForEl(contentDocument.body, colorTheme);
  }
};

const updateColorThemeClassNamesInSameOriginIframes = (iframes: Set<HTMLIFrameElement>, colorTheme: ColorTheme) => {
  iframes.forEach((iframe) => {
    iframe.addEventListener("load", () => {
      updateColorThemeClassNamesInIframe(iframe, colorTheme);
    });
    updateColorThemeClassNamesInIframe(iframe, colorTheme);
  });
};

export const updateColorThemeClassNamesInDocument = (
  document: Document,
  sameOriginIframeSet: Set<HTMLIFrameElement>,
  colorTheme: ColorTheme
) => {
  updateColorThemeClassNamesForEl(document.body, colorTheme);
  updateColorThemeClassNamesInSameOriginIframes(sameOriginIframeSet, currentColorTheme);
};

const postColorThemeChangedMessageToCrossOriginIframes = (iframes: Set<HTMLIFrameElement>, event: AnyMessageEvent) => {
  if (iframes && iframes.size) {
    iframes.forEach((iframe) => {
      if (!iframe.src.includes("/lotus/csp/")) {
        iframe.contentWindow && iframe.contentWindow.postMessage(event.data, "*");
      }
    });
  }
};

/**
 *
 * Event handler which is registered in the iframe utils
 *
 * 1. Updates the subscribers with the change,
 * 2. Updates the color theme for document and same origin iframes
 * 2. Posts a message to cross origin iframes
 */
export const handleColorThemeChange = ({
  event,
  sameOriginIframeSet,
  crossOriginIframeSet,
  window,
}: HandlerPayload<ColorThemeChangeEventPayload>) => {
  currentColorTheme = event.data?.payload?.colorTheme || ColorTheme.Light;
  updateColorThemeToSubscribers(event.data?.payload as ColorThemeChangeEventPayload);
  updateColorThemeClassNamesInDocument(window.document, sameOriginIframeSet, currentColorTheme);
  postColorThemeChangedMessageToCrossOriginIframes(crossOriginIframeSet, event);
};

const getColorThemeChangedEventData = (colorTheme: ColorTheme): AnyMessageEventData<{ colorTheme: ColorTheme }> => ({
  type: COLOR_THEME_CHANGED_EVENT,
  payload: {
    colorTheme,
  },
});

/**
 *
 * Subscriber for DOMMutations registered in the iframe utils
 *
 * 1. Updates color theme for newly added same origin iframes
 * 2. Posts message to newly added cross origin iframes
 */
const applyCurrentColorThemeToNewIframes = (
  sameOriginIframeSet: Set<HTMLIFrameElement>,
  crossOriginIframeSet: Set<HTMLIFrameElement>
) => {
  updateColorThemeClassNamesInSameOriginIframes(sameOriginIframeSet, currentColorTheme);
  const data = getColorThemeChangedEventData(currentColorTheme);
  postColorThemeChangedMessageToCrossOriginIframes(crossOriginIframeSet, { data } as AnyMessageEvent);
};

export const postColorThemeChangedMessageToWindow = (window: Window, colorTheme: ColorTheme) => {
  const data = getColorThemeChangedEventData(colorTheme);
  window.postMessage(data, "*");
};

export const onColorThemeChanged = (subscriber: OnColorThemeChangedSubscriberFn): OnColorThemeChanged => {
  // This avoids re-initializing colorThemeTracking incase of multiple subscribers from the same consumer
  if (!isColorThemeTracked) {
    startColorThemeTracking();
    isColorThemeTracked = true;
  }
  subscribers.add(subscriber);

  return {
    unsubscribe: () => subscribers.delete(subscriber),
  };
};

export const createPreferredColorThemeUtilsForBrowser = (window: Window) => {
  return {
    getPreferredColorTheme: () => {
      return window.matchMedia(BROWSER_DARK_MODE_QUERY)?.matches ? ColorTheme.Dark : ColorTheme.Light;
    },
    onPreferredColorThemeChange(listener: EventListener) {
      const matchMedia = window.matchMedia(BROWSER_DARK_MODE_QUERY);
      matchMedia.addEventListener("change", listener);

      return {
        unsubscribe() {
          matchMedia.removeEventListener("change", listener);
        },
      };
    },
  };
};

export const getColorTheme = () => {
  let colorTheme = ColorTheme.Light;
  // Sometimes there can be a race condition where the changes are not propagated through the window yet
  if (window?.document.body.classList.contains(POLARIS_DARK_MODE_CLASS)) {
    colorTheme = ColorTheme.Dark;
  }
  return colorTheme;
};

export const startColorThemeTracking = () => {
  startWindowHandler(window);
  registerEventHandler<ColorThemeChangeEventPayload>(COLOR_THEME_CHANGED_EVENT, handleColorThemeChange);
  subscribeForDOMMutations(applyCurrentColorThemeToNewIframes);
};
